/**
 * `/alerts/analytics` — workload and outcomes for site leads and supervisors.
 *
 * Answers: how much alert work exists now, how much arrived in the period, how
 * much got reviewed, what the outcomes were, and whether the day's shifts were
 * covered. It is not the operator queue, not ingest monitoring, and not model
 * performance reporting.
 *
 * Day is the default mode because only a local-day response carries a shift
 * breakdown — an arbitrary absolute range does not map onto one shift schedule.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/ui/components/ui/button";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { cn } from "@/ui/lib/utils";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { SETTINGS_URL } from "@/settings/config/settings-urls";
import { useAlertAnalytics } from "@/alerts/api/queries/alerts-analytics.queries";
import {
  alertTimeRangeErrorMessage,
  isLocationTimezoneMissing,
} from "@/alerts/api/time-range-errors";
import { ANALYTICS_MAX_LOOKBACK_DAYS } from "@/alerts/types/alerts-analytics";
import { buildShiftReviewSections } from "@/alerts/utils/reviewer-breakdown";
import { waitedFor } from "@/alerts/utils/format";
import { AlertsScopeGate } from "@/alerts/components/workspace/alerts-scope-gate";
import { ScopeSelect } from "@/alerts/components/workspace/alert-filter-controls";
import { AlertFlowChart } from "@/alerts/components/analytics/alert-flow-chart";
import { AlertOutcomesChart } from "@/alerts/components/analytics/alert-outcomes-chart";
import { AlertShiftCoverage } from "@/alerts/components/analytics/alert-shift-coverage";
import type { AlertAnalyticsFilterState } from "@/alerts/types/alerts-analytics";
import { useAlertsBreadcrumbs } from "@/alerts/hooks/useAlertsBreadcrumbs";

type TimeMode = "day" | "range";

/** ISO date (yyyy-mm-dd) N days before today, for the picker's floor. */
const isoDay = (offsetDays = 0): string =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);

const Panel = ({
  title,
  hint,
  children,
  className,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={cn("rounded-lg border border-border p-4", className)}>
    <div className="mb-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
    {children}
  </section>
);

const AlertsAnalyticsPage = () => {
  const { t } = useTranslation("alerts");
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const { value: range } = useDateTimeRangeStore();
  const locationsQuery = useGetAllLocations();
  const locations = useMemo(() => locationsQuery.data?.data ?? [], [locationsQuery.data]);

  const [locationId, setLocationId] = useState<number | null>(null);
  const [mode, setMode] = useState<TimeMode>("day");
  const [localDate, setLocalDate] = useState(() => isoDay());
  const [selectedShift, setSelectedShift] = useState<string | null>(null);

  useEffect(() => {
    if (locations.length === 0) return;
    if (!locations.some((location) => location.id === locationId)) setLocationId(locations[0].id);
  }, [locations, locationId]);

  // `setLocationId` is a `useState` setter — stable identity, as the crumb's
  // `onSelect` requires (see useAlertsBreadcrumbs).
  useAlertsBreadcrumbs({ locationId, onSelectLocation: setLocationId });

  // Leaving Day mode makes a shift selection meaningless — a range carries no
  // shift breakdown to select from.
  useEffect(() => {
    if (mode !== "day") setSelectedShift(null);
  }, [mode]);

  const filterState: AlertAnalyticsFilterState | null =
    locationId == null
      ? null
      : {
          scope: { location_id: locationId },
          period:
            mode === "day"
              ? { mode: "day", local_date: localDate }
              : {
                  mode: "range",
                  start_time: range.fromDate.toISOString(),
                  end_time: range.toDate.toISOString(),
                },
        };

  const query = useAlertAnalytics(filterState);
  const data = query.data;
  const shifts = data?.shift_breakdown ?? null;
  // Not memoised: it is a map over a handful of shifts, and the sections carry
  // translated shift and reviewer names — a cache keyed on the data alone would
  // hold the previous language after a switch.
  const shiftSections = buildShiftReviewSections(shifts);

  // Whole day, or one backend-provided shift series. Never sliced client-side:
  // a UTC bucket can straddle a local shift boundary or a DST change, so only
  // the server's shift series is correct.
  const activeShift = selectedShift ? shifts?.find((s) => s.shift_key === selectedShift) : null;
  const activeSummary = activeShift ? activeShift.period_summary : data?.period_summary;
  const timezone = null; // The backend already returns shift windows in local terms.

  const timezoneMissing = query.isError && isLocationTimezoneMissing(query.error);

  return (
    <AlertsScopeGate
      organizationId={organizationId}
      locations={locations}
      isLoading={locationsQuery.isPending}
      isError={locationsQuery.isError}
    >
      <div className="space-y-4 overflow-y-auto p-4">
        <div className="flex flex-wrap items-center gap-2">
          <ScopeSelect
            label={t("filters.labels.location")}
            placeholder={t("filters.labels.locationPlaceholder")}
            value={locationId}
            options={locations.map((location) => ({ value: location.id, label: location.name }))}
            onChange={setLocationId}
          />

          <span className="flex items-center rounded-md border border-border p-0.5 text-xs">
            {(
              [
                { id: "day", labelKey: "analytics.mode.day" },
                { id: "range", labelKey: "analytics.mode.range" },
              ] as const
            ).map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setMode(option.id)}
                className={cn(
                  "rounded px-2 py-1 transition-colors",
                  mode === option.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </span>

          {mode === "day" ? (
            <input
              type="date"
              aria-label={t("analytics.localDate")}
              value={localDate}
              // The picker itself enforces the horizon, so an out-of-range day
              // is normally unreachable; the backend error path stays as the
              // backstop for a hand-edited value.
              min={isoDay(-ANALYTICS_MAX_LOOKBACK_DAYS)}
              max={isoDay()}
              onChange={(event) => setLocalDate(event.target.value)}
              className="h-8 rounded-md border border-border bg-background px-2 text-xs"
            />
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {t("analytics.rangeHint", { days: ANALYTICS_MAX_LOOKBACK_DAYS })}
            </span>
          )}
        </div>

        {/* Current workload is ALWAYS now, even while a past day is selected —
            banded separately so it cannot be read as part of the period. */}
        <Panel title={t("analytics.now.title")} hint={t("analytics.now.hint")}>
          {query.isPending ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-lg font-semibold tabular-nums">
                  {data?.current_workload.pending_count ?? "—"}
                </p>
                <p className="text-[11px] text-muted-foreground">{t("analytics.now.pending")}</p>
              </div>
              <div>
                <p className="text-lg font-semibold tabular-nums">
                  {data?.current_workload.oldest_pending
                    ? `${data.current_workload.oldest_pending.estimated ? "~" : ""}${waitedFor(
                        data.current_workload.oldest_pending.waiting_since,
                        data.as_of,
                      )}`
                    : "—"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {t("analytics.now.oldestPending")}
                </p>
              </div>
              <div>
                <p className="text-lg font-semibold tabular-nums">
                  {/* A null count is "could not be computed", never zero. */}
                  {data?.current_workload.carryover.count ?? t("analytics.now.unavailable")}
                </p>
                <p className="text-[11px] text-muted-foreground">{t("analytics.now.carryover")}</p>
              </div>
            </div>
          )}
        </Panel>

        {query.isError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div className="min-w-0">
                {/* A rejected PERIOD must never render as an ordinary empty chart. */}
                <p className="text-sm font-medium">{alertTimeRangeErrorMessage(query.error)}</p>
                {timezoneMissing && (
                  <Button asChild size="sm" variant="outline" className="mt-2 h-7 gap-1.5 text-xs">
                    <Link to={SETTINGS_URL.LOCATION}>
                      <Settings2 className="h-3.5 w-3.5" />
                      {t("analytics.openLocationSettings")}
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <Panel title={t("analytics.flow.title")} hint={t("analytics.flow.hint")}>
              {mode === "day" && shiftSections.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedShift(null)}
                    className={cn(
                      "rounded-md border px-2 py-1 text-xs transition-colors",
                      selectedShift == null
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {t("analytics.flow.wholeDay")}
                  </button>
                  {shiftSections.map((section) => (
                    <button
                      key={section.shiftKey}
                      type="button"
                      onClick={() => setSelectedShift(section.shiftKey)}
                      className={cn(
                        "rounded-md border px-2 py-1 text-xs transition-colors",
                        selectedShift === section.shiftKey
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {section.label}
                      {section.status === "in_progress" && (
                        <span className="ml-1 text-[10px]">
                          · {t("analytics.flow.inProgress")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {query.isPending ? (
                <Skeleton className="h-56 w-full" />
              ) : activeSummary ? (
                <AlertFlowChart
                  trend={activeSummary.trend}
                  // Boundaries only make sense on the whole-day view.
                  shifts={selectedShift == null ? shifts : null}
                  timezone={timezone}
                />
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">
                  {t("analytics.flow.empty")}
                </p>
              )}
            </Panel>

            <Panel title={t("analytics.outcomes.title")} hint={t("analytics.outcomes.hint")}>
              {query.isPending ? (
                <Skeleton className="h-40 w-full" />
              ) : activeSummary ? (
                <AlertOutcomesChart
                  items={activeSummary.outcomes.items}
                  reviewed={activeSummary.outcomes.alerts_reviewed}
                  legacyDefaultedCount={activeSummary.outcomes.legacy_defaulted_disposition_count}
                />
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">
                  {t("analytics.outcomes.empty")}
                </p>
              )}
            </Panel>

            <Panel
              title={t("analytics.coverage.title")}
              hint={
                mode === "day"
                  ? t("analytics.coverage.hintDay")
                  : t("analytics.coverage.hintRange")
              }
            >
              {query.isPending ? (
                <Skeleton className="h-32 w-full" />
              ) : shiftSections.length > 0 ? (
                <AlertShiftCoverage sections={shiftSections} />
              ) : activeSummary ? (
                // Range mode has no shift breakdown — one period block rather
                // than a grid of empty shift cards.
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-lg font-semibold tabular-nums">
                      {activeSummary.queue_flow.alerts_received}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{t("labels.received")}</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold tabular-nums">
                      {activeSummary.queue_flow.alerts_reviewed}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{t("labels.reviewed")}</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold tabular-nums">
                      {activeSummary.reviewer_breakdown.reviewers.length}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{t("labels.reviewers")}</p>
                  </div>
                </div>
              ) : (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  {t("analytics.coverage.empty")}
                </p>
              )}
            </Panel>
          </>
        )}
      </div>
    </AlertsScopeGate>
  );
};

export default AlertsAnalyticsPage;
